import { Check } from 'lucide-react'
import { Badge } from '@/components/ui'
import type { CandidateTest, CandidateScriptedTier } from '@/types'
import { PRIORITY_BADGE } from '@/lib/priorityBadge'

/**
 * CandidatePickerPanel (#1452, Slice 2 of #1439; reworked in #1471) — the
 * interactive starting point for the discovery run.
 *
 * The app's candidate tests (already priority-ordered by the server, #1440) are
 * shown as a tickable list. The user picks the ones they want made into tests.
 * #1609 — each pick SEEDS the planner: the planner plans and live-verifies the
 * pick (grounding selectors, discovering data fields), then links the produced
 * test back to the candidate by the candidate's UUID so the per-candidate "tests
 * created" count is exact (#1471). The ticks are SAVED on the template (as
 * `pickedCandidateIds`) — earlier slices only used them to write prose into the
 * Agent Prompt box, which let the planner reword and lose the link. The picks no
 * longer touch the Agent Prompt; that box is now only for extra instructions
 * beyond the picked tests.
 *
 * Controlled: the dialog owns `selectedIds` and passes it down with a single
 * `onChange`. A row is itself the checkbox (`role="checkbox"` button) rather
 * than a nested `<Checkbox>` inside a clickable row — that keeps the whole row
 * clickable with no nested interactive elements and one clean accessible
 * control per candidate.
 *
 * #1478 — candidates that already have a test in the selected variant
 * (`scriptedTierById[id]` is anything other than `'proposed'`) are shown
 * disabled with an "Already scripted"/"Already added" badge, so the picker
 * stops offering work that's already done.
 */

export interface CandidatePickerPanelProps {
  /** Candidates for the app, in priority order (as returned by the server). */
  candidates: CandidateTest[]
  /** Currently-ticked candidate ids. */
  selectedIds: Set<string>
  /** Emit the next selection set. The panel never mutates `selectedIds`. */
  onChange: (next: Set<string>) => void
  /** Per-candidate scripted tier in the selected variant (#1478). A tier
   * other than `'proposed'` means the candidate already has a test here, so
   * the row is badged and un-tickable. Absent map / absent key → pickable. */
  scriptedTierById?: Record<string, CandidateScriptedTier>
  /** Pre-run coverage (#1626): for a candidate already covered by a
   * DIFFERENT existing test (cosine ≥ the duplicate threshold), the matched
   * test name + score. Drives the amber "already covered" badge — a heads-up
   * that the pick would likely be deduped, NOT a block (the operator can
   * still pick it). Absent key → no badge. */
  coverageByCandidate?: Record<string, { name: string; score: number }>
}

/** A candidate already has a test in this variant when its tier is past
 * 'proposed'. 'planned' = a test row exists but isn't scripted yet;
 * 'scripted'/'ready' = a spec is written. */
function alreadyHasTest(tier: CandidateScriptedTier | undefined): boolean {
  return tier !== undefined && tier !== 'proposed'
}

export function CandidatePickerPanel({ candidates, selectedIds, onChange, scriptedTierById, coverageByCandidate }: CandidatePickerPanelProps) {
  // Empty state — keep the existing nudge to upload reference docs. No panel,
  // no checkboxes (Slice 4 adds a scan-based fallback here).
  if (candidates.length === 0) {
    return (
      <p className="text-[10px] text-muted-foreground italic">
        Nothing proposed yet. Tests we suggest from your documents appear here — add
        one under Tests → Proposed and they will show up next time.
      </p>
    )
  }

  const toggle = (id: string) => {
    // A candidate that already has a test in this variant can't be picked
    // FRESH — but if it's already in the selection (a saved pick that has
    // since been scripted), allow unticking it so the operator isn't stuck
    // (Copilot review on #1479). New selection of a done row is blocked;
    // deselection of an already-checked one is allowed.
    if (alreadyHasTest(scriptedTierById?.[id]) && !selectedIds.has(id)) return
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }
  const clear = () => onChange(new Set())

  const count = selectedIds.size

  return (
    <div className="rounded border border-border bg-muted/30 text-xs">
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-border">
        <span className="font-medium">Start from your reference-doc tests</span>
        <span className="text-muted-foreground">· {candidates.length} found, highest priority first</span>
        <span className="text-muted-foreground" data-testid="selected-count">· {count} selected</span>
        <div className="ml-auto flex items-center gap-3">
          <button type="button" onClick={clear} className="text-primary font-medium hover:underline">
            Clear
          </button>
        </div>
      </div>

      <ul className="divide-y divide-border max-h-48 overflow-y-auto">
        {candidates.map(c => {
          const tier = scriptedTierById?.[c._id]
          const done = alreadyHasTest(tier)
          const checked = selectedIds.has(c._id)
          // A done row is locked only when it ISN'T already selected — a
          // checked-but-now-scripted saved pick stays operable so it can be
          // unticked (Copilot review on #1479).
          const locked = done && !checked
          const doneLabel = tier === 'planned' ? 'Already added' : 'Already scripted'
          // #1626 — show the "already covered" badge only on a pickable
          // (not-yet-scripted) row; a done row already carries the green
          // badge. Informational; the row stays tickable.
          const coverage = !done ? coverageByCandidate?.[c._id] : undefined
          return (
            <li key={c._id}>
              <button
                type="button"
                role="checkbox"
                aria-checked={checked}
                aria-label={c.name}
                // `aria-disabled` (not the native `disabled` attr) so a locked
                // row still surfaces its hover tooltip — the native attr
                // suppresses pointer events and would hide it. The click is
                // already a no-op for locked rows in `toggle`. Matches the
                // phase-pill pattern (Copilot review on #1607).
                aria-disabled={locked}
                // Full name + description on hover — the row truncates both, so
                // the tooltip is how the operator reads a long test's complete
                // text. Append the locked reason when the row is un-pickable.
                title={`${c.name}${c.description ? ` — ${c.description}` : ''}${
                  locked ? ` (${doneLabel} in this variant)` : ''
                }${
                  coverage ? ` (possible duplicate of "${coverage.name}" — ${coverage.score.toFixed(2)} similar)` : ''
                }`}
                onClick={() => toggle(c._id)}
                className={`w-full min-w-0 flex items-center gap-2 px-2 py-1.5 text-left ${
                  locked ? 'opacity-60 cursor-default' : 'hover:bg-muted/50'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center ${
                    checked ? 'bg-primary text-primary-foreground border-primary' : 'border-muted-foreground'
                  }`}
                >
                  {checked && <Check className="h-3 w-3" />}
                </span>
                {c.priority && (
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${PRIORITY_BADGE[c.priority] || ''}`}>
                    {c.priority}
                  </Badge>
                )}
                <span className="font-medium truncate min-w-0">{c.name}</span>
                <span className="text-muted-foreground truncate min-w-0">{c.description}</span>
                {done && (
                  <Badge variant="outline" className="ml-auto shrink-0 text-[10px] text-green-700 dark:text-green-400 border-green-300 dark:border-green-800">
                    {doneLabel}
                  </Badge>
                )}
                {coverage && (
                  // Names the matched existing test + score (per the
                  // mockup/AC). Truncates with a max-width so a long test
                  // name can't blow out the tight row; the full text lives
                  // in the row's hover title above.
                  <Badge
                    variant="outline"
                    className="ml-auto shrink min-w-0 max-w-[45%] truncate block text-[10px] text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800"
                  >
                    possible duplicate of &ldquo;{coverage.name}&rdquo; ({coverage.score.toFixed(2)})
                  </Badge>
                )}
              </button>
            </li>
          )
        })}
      </ul>

      {count > 0 && (
        <div className="flex items-center gap-2 px-2 py-1.5 border-t border-border">
          {/* #1609 — picks seed the plan; the planner plans and checks each
              one in a live browser. Wording reflects "will be planned," not
              an instant create, so the operator knows a pick run plans like
              any other. */}
          <span className="font-medium" data-testid="will-create-summary">
            {count} selected {count === 1 ? 'test' : 'tests'} will be planned
          </span>
          <span className="ml-auto text-amber-700 dark:text-amber-400">Selecting more tests makes the run longer.</span>
        </div>
      )}
    </div>
  )
}
